import { useId, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  BandReveal,
  HeroReveal,
  InteractiveDots,
  Pressable,
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  TextReveal,
} from '@/components/motion'
import { DocThumb, DockMarquee, PRODUCT_TILES, StatsRow } from '@/components/landing/service'
import { BRAND, halftone } from '@/components/landing/service/halftone'
import { HALFTONE_STYLE, Highlight } from '@/components/landing/about/Highlight'
import type { SolutionData } from '@/data/solutions'
import { StepJourney } from './StepJourney'
import { FADED, QUOTE, SolutionCta, splitLastWord, type SolutionLayoutProps } from './shared'

const DOC_ACCENTS = ['blue', 'purple', 'green', 'pink'] as const
/** Brand tiles that sit between the team's documents in the marquee. */
const BRAND_TILES = ['swatches', 'assets', 'powerpoint', 'taskpane', 'word', 'chart', 'outlook']

/** The marquee: the team's documents, interleaved with brand and app tiles. */
function marqueeTiles(solution: SolutionData): ReactNode[] {
  const extras = BRAND_TILES.map((key) => PRODUCT_TILES.find((t) => t.key === key)?.node).filter(Boolean)
  const docs = [...solution.documents, ...solution.documents.slice(0, 3)]
  const tiles: ReactNode[] = []
  docs.forEach((doc, i) => {
    tiles.push(
      <DocThumb
        key={`doc-${i}`}
        kind={doc.kind}
        title={doc.title}
        accent={DOC_ACCENTS[(i + Math.floor(i / 4)) % DOC_ACCENTS.length]}
        className="h-full w-full"
      />
    )
    if (extras[i]) tiles.push(extras[i])
  })
  return tiles
}

/** Feature tile backdrops: blue, pink, purple, black (blue in dark mode). */
const FEATURE_BG = ['var(--color-landing-accent)', '#ff00f2', 'var(--color-landing-secondary)', '#0a0a0a'] as const

/** Full-width FAQ: hairline rows, the question large, one answer open at a time. */
function WideFaq({ items }: { items: SolutionData['faq'] }) {
  const [open, setOpen] = useState<number | null>(null)
  const uid = useId()
  return (
    <ul className="mt-10 border-t border-[var(--color-landing-gray-900)] dark:border-white/70 md:mt-14">
      {items.map((item, i) => {
        const isOpen = open === i
        const qId = `${uid}-q${i}`
        const aId = `${uid}-a${i}`
        return (
          <li key={item.question} className="border-b border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]">
            <h3>
              <button
                id={qId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={aId}
                onClick={() => setOpen(isOpen ? null : i)}
                className="group grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-6 py-7 text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-landing-accent)] md:py-9 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_auto]"
              >
                <span className="font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.01em] text-[var(--color-landing-gray-900)] transition-colors duration-200 group-hover:text-[#ff00f2] dark:text-white md:text-2xl">
                  {item.question}
                </span>
                <span aria-hidden className="hidden lg:block" />
                <span
                  aria-hidden
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center border transition-[background-color,border-color,color] duration-200',
                    isOpen
                      ? 'border-[#ff00f2] bg-[#ff00f2] text-white'
                      : 'border-[var(--color-landing-gray-900)] text-[var(--color-landing-gray-900)] dark:border-white dark:text-white'
                  )}
                >
                  <Plus
                    className={cn('h-5 w-5 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]', isOpen && 'rotate-45')}
                  />
                </span>
              </button>
            </h3>
            <div
              id={aId}
              role="region"
              aria-labelledby={qId}
              inert={!isOpen}
              className="grid transition-[grid-template-rows] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
            >
              <div className="overflow-hidden">
                <p className="landing-text-body-lg max-w-[62ch] pb-9 text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)] lg:ml-[calc(50%+1.5rem)] lg:max-w-none lg:pr-16">
                  {item.answer}
                </p>
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

const monogram = (name: string) =>
  name
    .split(/[\s&]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

/**
 * Creative-content layout (blue2purple layout 2), used by Marketing: white
 * hero with the team in a pink box, a dock-magnify marquee of the team's
 * documents, a pink stats band, the challenge, features as a sticky-left
 * tile grid, the steps as the about-page journey timeline, a full-width FAQ
 * with the customer story as a wide banner, and the purple CTA band.
 */
export function CreativeLayout({ solution, story }: SolutionLayoutProps) {
  const [featLead, featLast] = splitLastWord(solution.featuresHeadline)
  const [probLead, probLast] = splitLastWord(solution.problemHeadline)
  const cs = solution.customerStory

  return (
    <>
      {/* 1. Hero: pink team box, 5rem H1, quote + button row, then the dock marquee */}
      <section className="overflow-hidden pt-16 md:pt-24 lg:pt-28">
        <div className="landing-container">
          <div className="lg:mx-auto lg:w-[80%]">
            <HeroReveal delay={0}>
              <p className="landing-text-display-xl">
                <span className="inline-block -rotate-1 bg-[#ff00f2] px-[0.25em] text-white">{solution.teamLabel}</span>
              </p>
            </HeroReveal>
            <HeroReveal delay={0.1}>
              <h1 className="landing-text-hero mt-4 text-[var(--color-landing-gray-900)] dark:text-white md:mt-6">
                <span className="block">{solution.headlineLines[0]}</span>{' '}
                <span className="block">{solution.headlineLines[1]}</span>
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.2} className="mt-10 flex flex-col gap-8 md:mt-12 md:flex-row md:items-center md:gap-12">
              <p className={cn(QUOTE, 'max-w-[62ch] text-pretty md:text-xl')}>{solution.subheadline}</p>
              <Pressable className="inline-block shrink-0 self-start md:self-center">
                <Link to="/contact" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                  Book a demo
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Pressable>
            </HeroReveal>
          </div>
        </div>
        <HeroReveal delay={0.35} distance={40}>
          <DockMarquee className="mt-8 md:mt-12" tiles={marqueeTiles(solution)} />
        </HeroReveal>
      </section>

      {/* 2. Stats band: pink, display-size figures */}
      <BandReveal>
        <section aria-label={`${solution.teamName} in numbers`} className="landing-section-lg relative overflow-hidden bg-[#ff00f2]">
          <InteractiveDots />
          <div className="landing-container relative z-10">
            <div className="lg:mx-auto lg:w-[80%]">
              <StatsRow stats={solution.heroStats} size="display" />
            </div>
          </div>
        </section>
      </BandReveal>

      {/* 3. The challenge: headline left, problem rows right */}
      <section className="landing-section">
        <div className="landing-container grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-20">
          <div>
            <TextReveal as="h2" className="landing-text-display-lg max-w-[22ch] text-[var(--color-landing-gray-900)] dark:text-white">
              {probLead} <Highlight color="purple">{probLast}</Highlight>
            </TextReveal>
            <ScrollReveal direction="none" delay={0.15}>
              <p className={cn(QUOTE, 'mt-8 max-w-[56ch] text-pretty')}>{solution.problemDescription}</p>
            </ScrollReveal>
          </div>
          <StaggerContainer amount={0.2} className="border-t border-[var(--color-landing-gray-900)] dark:border-white/70 lg:self-end">
            {solution.problemPoints.map((point, i) => {
              const Icon = point.icon
              return (
                <StaggerItem
                  key={point.title}
                  className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-6 border-b border-[var(--color-landing-gray-200)] py-7 dark:border-[var(--color-landing-gray-800)]"
                >
                  <span
                    aria-hidden
                    className="flex h-12 w-12 items-center justify-center text-white"
                    style={{ backgroundColor: [BRAND.pink, BRAND.blue, BRAND.purple][i % 3], ...halftone(0.28, 5) }}
                  >
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3 className="font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
                      {point.title}
                    </h3>
                    <p className={cn('landing-text-body mt-2', FADED)}>{point.description}</p>
                  </div>
                </StaggerItem>
              )
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* 4. Features: sticky left column, tiles on brand backdrops right (about's team grid) */}
      <section aria-labelledby="features-title" className="landing-section !pt-0">
        <div className="landing-container grid items-start gap-14 lg:grid-cols-[minmax(0,0.4fr)_minmax(0,1fr)] lg:gap-20">
          <div className="lg:sticky lg:top-28">
            <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
              <span id="features-title">
                {featLead} <Highlight color="blue">{featLast}</Highlight>
              </span>
            </TextReveal>
            <ScrollReveal direction="none" delay={0.15}>
              <p className={cn(QUOTE, 'mt-8 max-w-[34ch]')}>{solution.featuresDescription}</p>
              <Link
                to="/register"
                className="group mt-10 inline-flex items-center gap-3 font-[family-name:var(--font-landing-display)] text-lg uppercase text-[var(--color-landing-gray-900)] transition-colors duration-200 hover:text-[var(--color-landing-accent)] dark:text-white"
              >
                Start free trial
                <ArrowRight aria-hidden className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
            </ScrollReveal>
          </div>

          <StaggerContainer amount={0.05} className="grid gap-x-5 gap-y-14 sm:grid-cols-2 md:gap-y-16">
            {solution.features.map((feature, i) => {
              const Icon = feature.icon
              return (
                <StaggerItem key={feature.title} className="group">
                  <div
                    aria-hidden
                    className={cn(
                      'relative aspect-[4/3] overflow-hidden [container-type:inline-size] dark:ring-1 dark:ring-inset dark:ring-white/10',
                      i === 3 && 'dark:!bg-[var(--color-landing-accent)]'
                    )}
                    style={{ background: FEATURE_BG[i % FEATURE_BG.length] }}
                  >
                    <div
                      className="absolute inset-0 opacity-60 transition-opacity duration-300 ease-out [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100"
                      style={HALFTONE_STYLE}
                    />
                    <Icon
                      className="absolute bottom-[8%] left-[6%] h-[34cqw] w-[34cqw] text-white transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] [@media(hover:hover)_and_(pointer:fine)]:group-hover:-translate-y-[3%]"
                      strokeWidth={1}
                    />
                  </div>
                  <h3 className="mt-5 font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white md:text-xl">
                    {feature.title}
                  </h3>
                  <p className={cn('landing-text-body mt-3 max-w-[46ch]', FADED)}>{feature.description}</p>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {feature.outcomes.map((outcome) => (
                      <li
                        key={outcome}
                        className="border border-[var(--color-landing-gray-300)] px-2.5 py-1 text-sm font-semibold text-[var(--color-landing-gray-800)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-200)]"
                      >
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </StaggerItem>
              )
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* 5. Steps: the journey timeline with the blend line */}
      <StepJourney solution={solution} />

      {/* 6. FAQ, full width */}
      <section className="landing-section">
        <div className="landing-container">
          <TextReveal as="h2" className="landing-text-hero text-[var(--color-landing-gray-900)] dark:text-white">
            FAQ
          </TextReveal>
          <WideFaq items={solution.faq} />
        </div>
      </section>

      {/* 7. Customer story as a wide banner */}
      <section aria-labelledby="story-banner-title" className="landing-section !pt-0">
        <div className="landing-container">
          <ScrollReveal className="relative overflow-hidden bg-[var(--color-landing-accent)] text-white">
            <span aria-hidden className="absolute inset-0" style={halftone(0.2, 7)} />
            <div className="relative grid gap-10 p-7 md:p-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)_auto] lg:items-end lg:gap-14 lg:p-14">
              <div className="flex flex-col gap-8">
                <span className="flex items-center gap-4">
                  <span
                    aria-hidden
                    className="flex h-16 w-16 shrink-0 items-center justify-center bg-white font-[family-name:var(--font-landing-display)] text-xl text-[var(--color-landing-accent)]"
                  >
                    {monogram(cs.company)}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm font-bold uppercase tracking-wide">{cs.company}</span>
                    <span className="mt-1 self-start bg-[#ff00f2] px-2 py-0.5 text-xs font-bold uppercase tracking-wide">{story.industry}</span>
                  </span>
                </span>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-white/80">{cs.improvement}</p>
                  <p className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 font-[family-name:var(--font-landing-display)] uppercase leading-[1.25]">
                    <span className="text-2xl text-white/60 line-through decoration-2">{cs.beforeMetric}</span>
                    <ArrowRight aria-hidden className="h-6 w-6 shrink-0 self-center" />
                    <span className="text-[clamp(2.5rem,5vw,4.5rem)]">{cs.afterMetric}</span>
                  </p>
                </div>
              </div>
              <div>
                <h3
                  id="story-banner-title"
                  className="font-[family-name:var(--font-landing-display)] text-xl uppercase leading-[1.4] md:text-2xl"
                >
                  {story.headline}
                </h3>
                <blockquote className="landing-text-body-lg mt-5 max-w-[60ch] text-pretty text-white/85">"{cs.quote}"</blockquote>
                <p className="mt-4 text-sm text-white">
                  <span className="font-semibold">{cs.author}</span>
                  <span className="text-white/75">, {cs.title}</span>
                </p>
              </div>
              <Link
                to="/customers"
                className="landing-cta landing-cta--solid landing-cta--md landing-cta--on-color landing-btn-sweep self-start border-[#ff00f2] bg-[#ff00f2] dark:border-[#ff00f2] dark:bg-[#ff00f2] dark:text-white lg:self-end"
              >
                Read the story
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 8. Purple CTA band */}
      <SolutionCta solution={solution} tone="purple" />
    </>
  )
}

export default CreativeLayout
